import { useState, useCallback } from 'react';
import { devLog, devError } from '../utils/devLog';
import { processImage } from '../utils/imageconvOptimized';
import { takeScreenshot } from '../utils/screenshot';
import { extractAIResponse } from '../utils/responseParser';
import { authedFetch } from '../api/authedFetch';

const EXTENDED_TIMEOUT_URL = 'https://api.limkc.com/extended/';

export default function useChatMedia({ setIsLoading, setMessages, selectedModel, currentSessionId, screenshotInProgress }) {
  const [selectedImages, setSelectedImages] = useState(() => {
    const savedImageData = localStorage.getItem('chatty_selectedImages');
    if (savedImageData) {
      try { return JSON.parse(savedImageData) || []; } catch (e) { return []; }
    }
    return [];
  });

  const [uploadImages, setUploadImages] = useState(() => {
    const savedUploadImages = localStorage.getItem('chatty_uploadImages');
    if (savedUploadImages) {
      try {
        const parsed = JSON.parse(savedUploadImages);
        return parsed.map(item => {
          const byteString = atob(item.data.split(',')[1]);
          const arrayBuffer = new ArrayBuffer(byteString.length);
          const uint8Array = new Uint8Array(arrayBuffer);
          for (let i = 0; i < byteString.length; i++) {
            uint8Array[i] = byteString.charCodeAt(i);
          }
          return new Blob([arrayBuffer], { type: item.type });
        });
      } catch (e) {
        devError('Error restoring upload images:', e);
        return [];
      }
    }
    return [];
  });

  const handleImageUpload = useCallback(async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const imageFiles = files.filter(file => file.type.startsWith("image/"));
    if (imageFiles.length === 0) {
      alert("Please upload image files.");
      return;
    }

    setIsLoading(true);
    try {
      const newPreviews = [];
      const newUploads = [];
      const newUploadData = [];

      for (const file of imageFiles) {
        const { previewImage, uploadImage } = await processImage(file);

        const previewBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(previewImage);
        });

        const uploadBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(uploadImage);
        });

        newPreviews.push(previewBase64);
        newUploads.push(uploadImage);
        newUploadData.push({ name: file.name, type: file.type, data: uploadBase64 });
      }

      setSelectedImages(prev => {
        const updated = [...prev, ...newPreviews];
        localStorage.setItem('chatty_selectedImages', JSON.stringify(updated));
        return updated;
      });

      setUploadImages(prev => {
        const updated = [...prev, ...newUploads];
        const existingData = JSON.parse(localStorage.getItem('chatty_uploadImages') || '[]');
        localStorage.setItem('chatty_uploadImages', JSON.stringify([...existingData, ...newUploadData]));
        return updated;
      });
    } catch (error) {
      devError("Error processing images:", error);
      alert("Error processing images. Try again.");
    } finally {
      setIsLoading(false);
    }
  }, [setSelectedImages, setUploadImages, setIsLoading]);

  const handleScreenshotRequest = useCallback(async (reason, originalMessage) => {
    devLog(`📸 Taking screenshot: ${reason}`);

    try {
      const screenshotDataUrl = await takeScreenshot({
        download: false, scale: 0.75, format: 'jpeg', quality: 0.7
      });

      devLog(`📸 Screenshot captured, size: ${Math.round(screenshotDataUrl.length / 1024)}KB`);

      const glowOverlay = document.createElement('div');
      glowOverlay.className = 'screenshot-border-glow';
      document.body.appendChild(glowOverlay);
      setTimeout(() => glowOverlay.remove(), 600);

      const base64Image = screenshotDataUrl.split(',')[1];
      const modelToUse = selectedModel === "kaidex-local" ? "gpt-4.1-mini" : selectedModel;

      const payload = {
        model: modelToUse,
        messages: [{
          role: "user",
          content: `[Screenshot tool result - you called this tool] User's original request: "${originalMessage}". Analyze the captured screen and respond helpfully.`,
          images: [base64Image]
        }],
        stream: false,
        session_id: currentSessionId,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        screenshot_continuation: true
      };

      devLog(`📸 Sending screenshot to AI (continuation mode), model: ${modelToUse}...`);

      const response = await authedFetch(EXTENDED_TIMEOUT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error(`API error: ${response.status}`);

      const data = await response.json();
      devLog(`📸 Screenshot analysis received:`, data);

      let aiText;
      if (typeof data.response === 'string') {
        aiText = data.response;
      } else if (data.response?.choices?.[0]?.message?.content) {
        aiText = data.response.choices[0].message.content;
      } else if (data.choices?.[0]?.message?.content) {
        aiText = data.choices[0].message.content;
      } else {
        aiText = "I analyzed the screenshot but couldn't generate a response.";
      }

      setMessages(prev => [...prev, { role: "ai", text: aiText }]);
      screenshotInProgress.current = false;
      setIsLoading(false);

    } catch (error) {
      console.error('📸 Screenshot failed:', error);
      setMessages(prev => [...prev, {
        role: "ai",
        text: `I tried to take a screenshot but encountered an error: ${error.message}. Please try again or describe what you're seeing.`
      }]);
      screenshotInProgress.current = false;
      setIsLoading(false);
    }
  }, [selectedModel, currentSessionId, setMessages, setIsLoading, screenshotInProgress]);

  return {
    selectedImages, setSelectedImages,
    uploadImages, setUploadImages,
    handleImageUpload,
    handleScreenshotRequest,
  };
}
